import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Stack,
  Typography,
  Select,
  MenuItem,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  styled,
  Pagination,
} from '@mui/material';

import type { SelectChangeEvent } from '@mui/material';
import type { TableData } from '@/@types/admin';

interface AdminDataTableProps {
  children?: Readonly<React.ReactNode>;
  data: TableData;
}

const CenteredTableContainer = styled(TableContainer)(
  ({ theme }) => `
  border: 1px solid ${theme.palette.divider};

  td, th {
    border-right: 1px solid ${theme.palette.divider};
    text-align: center;

    &:last-of-type {
      border-right: none;
    }
  }
`,
);

export default function AdminDataTable({ children, data }: AdminDataTableProps) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = Math.ceil(data.rows.length / rowsPerPage);
  const pagedRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < data.rows.length; i += rowsPerPage) {
      rows.push(data.rows.slice(i, i + rowsPerPage));
    }
    return rows;
  }, [data.rows, rowsPerPage]);

  function onSelectChange(e: SelectChangeEvent) {
    setCurrentPage(1);
    setRowsPerPage(Number(e.target.value));
  }
  function onPageChange(_: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
  }

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography sx={{ flexGrow: 1 }}>
          검색결과 총&nbsp;
          <Typography component="span" color="primary" fontWeight="bold">
            {data.rows.length}건
          </Typography>
          이 있습니다.
        </Typography>
        {children}
        <Select value={rowsPerPage.toString()} onChange={onSelectChange} sx={{ width: 160, height: 40 }}>
          <MenuItem value="10">10개씩</MenuItem>
          <MenuItem value="20">20개씩</MenuItem>
          <MenuItem value="50">50개씩</MenuItem>
          <MenuItem value="100">100개씩</MenuItem>
        </Select>
      </Stack>

      <CenteredTableContainer sx={{ borderColor: 'divider' }}>
        <Table>
          <TableHead>
            <TableRow>
              {data.header.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows[currentPage - 1]?.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{rowsPerPage * (currentPage - 1) + i + 1}</TableCell>
                {data.keys.map((key) => {
                  if (key === data.linkKey)
                    return (
                      <TableCell key={key}>
                        <Link to={data.linkUrl} state={{ title: data.linkTitle, id: row.id }}>
                          {row[key]}
                        </Link>
                      </TableCell>
                    );
                  return <TableCell key={key}>{row[key]}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CenteredTableContainer>
      <Pagination
        size="large"
        showFirstButton
        showLastButton
        page={currentPage}
        count={totalCount}
        onChange={onPageChange}
        sx={{ alignSelf: 'center' }}
      />
    </>
  );
}
